/**
 * CookieConsent.tsx - Integrated.Social
 *
 * Geo-aware consent banner with granular Manage Preferences panel:
 * - EU/UK visitors only (detected via Intl timezone - mirrors index.html logic)
 * - Yandex choice required worldwide, independently of existing Google defaults
 *
 * Four consent actions:
 *   1. Accept All - grants analytics + advertising
 *   2. Decline All - denies analytics + advertising (quick opt-out)
 *   3. Essential Only - alias for Decline All (legacy, kept for compatibility)
 *   4. Manage Preferences - granular toggles per category, then Save
 *
 * localStorage schema:
 *   cookie-consent = "all" | "essential" | "custom"
 *   cookie-consent-analytics = "granted" | "denied"
 *   cookie-consent-advertising = "granted" | "denied"
 *   cookie-consent-yandex = "granted" | "denied" (explicit, fresh v2.0 record only)
 *
 * Consent categories:
 *   Analytics    → analytics_storage
 *   Advertising  → ad_storage + ad_user_data + ad_personalization
 */

import { useState, useEffect } from "react";
import { BarChart2, X, SlidersHorizontal, ChevronDown, ChevronUp } from "lucide-react";

/** Mirror of the timezone detection logic in index.html */
function isEuUkVisitor(): boolean {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const euPrefixes = [
      "Europe/",
      "Atlantic/Reykjavik",
      "Atlantic/Faroe",
      "Atlantic/Canary",
      "Africa/Ceuta",
      "Arctic/Longyearbyen",
    ];
    return euPrefixes.some((p) => tz.startsWith(p));
  } catch {
    return true; // fail-safe: treat as EU if detection throws
  }
}

/** Apply a consent state to gtag */
function applyConsent(analytics: "granted" | "denied", advertising: "granted" | "denied") {
  if (typeof window !== "undefined" && (window as any).gtag) {
    (window as any).gtag("consent", "update", {
      analytics_storage: analytics,
      ad_storage: advertising,
      ad_user_data: advertising,
      ad_personalization: advertising,
    });
  }
}

/** Keep the consent-gated OpenAI advertising SDK synchronized after preference changes. */
function notifyOpenAiAdvertisingConsentChange() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("openai-advertising-consent-changed"));
  }
}

/** Read saved granular prefs from localStorage */
function getSavedPrefs(): { analytics: boolean; advertising: boolean } {
  try {
    return {
      analytics: localStorage.getItem("cookie-consent-analytics") !== "denied",
      advertising: localStorage.getItem("cookie-consent-advertising") !== "denied",
    };
  } catch {
    return { analytics: true, advertising: true };
  }
}

type Panel = "main" | "preferences";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [panel, setPanel] = useState<Panel>("main");
  // Analytics defaults to ON (legitimate interest - GDPR Art.6(1)(f)).
  // Advertising defaults to OFF for EU/UK (opt-in required - GDPR/PECR).
  // Users can opt out of analytics at any time via this panel.
  const [analyticsOn, setAnalyticsOn] = useState(true);
  const [advertisingOn, setAdvertisingOn] = useState(false);
  const [yandexOn, setYandexOn] = useState(false);
  const [showCategoryDetails, setShowCategoryDetails] = useState<string | null>(null);

  useEffect(() => {
    let consent: string | null = null;
    try {
      consent = localStorage.getItem("cookie-consent");
      // Preserve the existing EU/UK Google expiry handling, without treating a
      // legacy Google choice as permission for Yandex analytics or replay.
      if (isEuUkVisitor() && consent) {
        const record = JSON.parse(localStorage.getItem("cookie-consent-record") || "{}");
        const ts = record.timestamp ? Date.parse(record.timestamp) : NaN;
        if (!Number.isFinite(ts) || ts > Date.now() || Date.now() - ts > 30 * 24 * 60 * 60 * 1000) {
          ["cookie-consent", "cookie-consent-analytics", "cookie-consent-advertising", "cookie-consent-record", "cookie-consent-yandex"].forEach((key) => localStorage.removeItem(key));
          consent = null;
          window.dispatchEvent(new Event("yandex-metrica-consent-changed"));
        }
      }
    } catch {
      consent = null; // Storage failure never implies Yandex consent.
    }
    if (isEuUkVisitor()) {
      if (consent === "all") applyConsent("granted", "granted");
      else if (consent === "custom") {
        const prefs = getSavedPrefs();
        applyConsent(prefs.analytics ? "granted" : "denied", prefs.advertising ? "granted" : "denied");
      }
    }
    // Every visitor can make a dedicated Yandex choice. Dismissal is not consent.
    const hasFreshYandexChoice = () => (window as any).__integratedYandexMetrica?.hasFreshChoice() === true;
    const timer = !hasFreshYandexChoice() ? setTimeout(() => setVisible(true), 4000) : undefined;
    const storageChanged = (event: StorageEvent) => {
      if ((!event.key || event.key.startsWith("cookie-consent")) && !hasFreshYandexChoice()) setVisible(true);
    };
    const choiceRequired = () => setVisible(true);
    window.addEventListener("storage", storageChanged);
    window.addEventListener("yandex-metrica-choice-required", choiceRequired);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("storage", storageChanged);
      window.removeEventListener("yandex-metrica-choice-required", choiceRequired);
    };
  }, []);

  const openPreferences = () => {
    const prefs = getSavedPrefs();
    setAnalyticsOn(prefs.analytics);
    setAdvertisingOn(prefs.advertising);
    setYandexOn((window as any).__integratedYandexMetrica?.isGranted() === true);
    setPanel("preferences");
  };

  /** Store a consent record with timestamp for audit purposes (GDPR Art.7(1)) */
  function recordConsent(choice: string, analytics: string, advertising: string, yandex: boolean) {
    const record = {
      choice,
      analytics,
      advertising,
      yandex,
      timestamp: new Date().toISOString(),
      version: "2.0",
    };
    try { localStorage.setItem("cookie-consent-record", JSON.stringify(record)); } catch {}
  }

  function storeChoice(choice: string, analytics: string, advertising: string, yandex: boolean) {
    const permitYandex = yandex && analytics === "granted" && (navigator as any).globalPrivacyControl !== true;
    try {
      localStorage.setItem("cookie-consent", choice);
      localStorage.setItem("cookie-consent-analytics", analytics);
      localStorage.setItem("cookie-consent-advertising", advertising);
      localStorage.setItem("cookie-consent-yandex", permitYandex ? "granted" : "denied");
      recordConsent(choice, analytics, advertising, permitYandex);
    } catch {
      // Do not enable a transient/in-memory Yandex grant when persistence fails.
      try { localStorage.removeItem("cookie-consent-yandex"); } catch {}
    }
    window.dispatchEvent(new Event("yandex-metrica-consent-changed"));
  }

  const acceptAll = () => {
    storeChoice("all", "granted", "granted", true);
    setVisible(false);
    applyConsent("granted", "granted");
    window.dispatchEvent(new Event("linkedin-consent-granted"));
    notifyOpenAiAdvertisingConsentChange();
  };

  const acceptEssential = () => {
    storeChoice("essential", "denied", "denied", false);
    setVisible(false);
    applyConsent("denied", "denied");
    notifyOpenAiAdvertisingConsentChange();
  };

  const closeBannerWithoutConsent = () => {
    // No storage writes. Existing Google defaults/choices are unchanged.
    setVisible(false);
  };

  const savePreferences = () => {
    const analyticsVal = analyticsOn ? "granted" : "denied";
    const advertisingVal = advertisingOn ? "granted" : "denied";
    storeChoice("custom", analyticsVal, advertisingVal, yandexOn);
    setVisible(false);
    applyConsent(analyticsVal, advertisingVal);
    if (advertisingVal === "granted") window.dispatchEvent(new Event("linkedin-consent-granted"));
    notifyOpenAiAdvertisingConsentChange();
  };

  /** Allow footer / settings link to reopen the banner */
  const reopenConsent = () => {
    setPanel("main");
    setVisible(true);
  };

  // Expose globally so Footer.tsx can call window.__openCookieConsent()
  useEffect(() => {
    (window as any).__openCookieConsent = reopenConsent;
    return () => { delete (window as any).__openCookieConsent; };
  }, []);

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999]"
      style={{ animation: "slideUp 0.3s cubic-bezier(0.23, 1, 0.32, 1) both" }}
    >
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .consent-toggle {
          position: relative;
          width: 40px;
          height: 22px;
          border-radius: 11px;
          cursor: pointer;
          transition: background 0.2s;
          flex-shrink: 0;
          border: none;
          outline: none;
        }
        .consent-toggle.on  { background: #FF8C00; }
        .consent-toggle.off { background: rgba(255,255,255,0.15); }
        .consent-toggle::after {
          content: '';
          position: absolute;
          top: 3px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: white;
          transition: left 0.2s;
        }
        .consent-toggle.on::after  { left: 21px; }
        .consent-toggle.off::after { left: 3px; }
      `}</style>

      {/* ─────────────────────────────────────────────────────
          MOBILE LAYOUT
      ───────────────────────────────────────────────────── */}
      <div className="md:hidden bg-[#0F1419] border-t border-[#FF8C00]/30 px-4 py-4 shadow-[0_-4px_24px_rgba(0,0,0,0.4)]">

        {panel === "main" && (
          <>
            <div className="flex items-start gap-3 mb-3">
              <div className="w-8 h-8 rounded-sm bg-[#FF8C00]/15 flex items-center justify-center shrink-0 mt-0.5">
                <BarChart2 className="w-4 h-4 text-[#FF8C00]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-semibold mb-0.5">Help us improve your experience</p>
                <p className="text-white/55 text-xs leading-snug">
                  Google analytics keeps its existing settings. Yandex analytics and session replay are off until you opt in. Replay can record page interactions; forms and chat are masked. Change your choice anytime.{" "}
                  <a href="/cookie-policy/" className="text-[#FF8C00] underline-offset-2 hover:underline">Cookie Policy</a>
                </p>
              </div>
              <button onClick={closeBannerWithoutConsent} className="text-white/30 hover:text-white/60 transition-colors shrink-0 p-1 -mt-0.5" aria-label="Close without changing preferences or granting Yandex consent">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex gap-2 mb-2">
              <button onClick={acceptAll} className="flex-1 px-3 py-2.5 bg-[#FF8C00] hover:bg-[#E67E00] text-[#0F1419] font-bold rounded-sm transition-colors text-xs active:scale-[0.97]">
                Accept all
              </button>
              <button onClick={acceptEssential} className="flex-1 px-3 py-2.5 bg-white/8 hover:bg-white/15 text-white/70 hover:text-white font-medium rounded-sm transition-colors text-xs border border-white/10">
                Decline all
              </button>
            </div>
            <button onClick={openPreferences} className="w-full flex items-center justify-center gap-1.5 py-2 text-white/40 hover:text-white/65 text-xs transition-colors">
              <SlidersHorizontal className="w-3 h-3" />
              Manage preferences
            </button>
          </>
        )}

        {panel === "preferences" && (
          <>
            <div className="flex items-center gap-2 mb-3">
              <button onClick={() => setPanel("main")} className="text-white/40 hover:text-white/70 transition-colors text-xs">← Back</button>
              <span className="text-white text-xs font-semibold flex-1 text-center">Cookie Preferences</span>
              <button onClick={closeBannerWithoutConsent} className="text-white/30 hover:text-white/60 transition-colors p-1" aria-label="Close without changing preferences or granting Yandex consent">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Essential - always on */}
            <MobileCategory label="Essential" description="Required for the site to function." locked />

            {/* Analytics */}
            <MobileCategory
              label="Analytics"
              description="Google Analytics 4 - helps us understand how visitors use the site. Data is anonymised."
              checked={analyticsOn}
              onToggle={() => setAnalyticsOn((v) => !v)}
            />

            <MobileCategory
              label="Yandex analytics and session replay"
              description="Off by default. Records public-page interactions after opt-in. Forms and chat are masked. Analytics denial or Global Privacy Control blocks Yandex."
              checked={yandexOn}
              onToggle={() => setYandexOn((v) => !v)}
            />

            {/* Advertising */}
            <MobileCategory
              label="Advertising"
              description="Google Ads, LinkedIn & OpenAI - measures ad performance and enables relevant ads."
              checked={advertisingOn}
              onToggle={() => setAdvertisingOn((v) => !v)}
            />

            <button onClick={savePreferences} className="w-full mt-3 px-3 py-2.5 bg-[#FF8C00] hover:bg-[#E67E00] text-[#0F1419] font-bold rounded-sm transition-colors text-xs active:scale-[0.97]">
              Save my preferences
            </button>
          </>
        )}
      </div>

      {/* ─────────────────────────────────────────────────────
          DESKTOP LAYOUT
      ───────────────────────────────────────────────────── */}
      <div className="hidden md:block px-6 pb-6">
        <div className="max-w-3xl mx-auto bg-[#0F1419] border border-[#FF8C00]/25 rounded-xl shadow-[0_-8px_40px_rgba(0,0,0,0.5)] p-6">

          {panel === "main" && (
            <>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-[#FF8C00]/15 flex items-center justify-center shrink-0 mt-0.5">
                  <BarChart2 className="w-5 h-5 text-[#FF8C00]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold text-base mb-1">Help us make this site better for you</h3>
                  <p className="text-white/55 text-sm leading-relaxed">
                    Google analytics keeps its existing settings. Yandex analytics and session replay require your separate opt-in, wherever you visit from. Replay can record page content, clicks and scrolling on public pages. Forms, chat and personal results are masked, and replay is off in tools and audit flows. Use Cookie Settings to withdraw at any time.
                  </p>
                </div>
                <button onClick={closeBannerWithoutConsent} className="text-white/30 hover:text-white/60 transition-colors p-1 shrink-0" aria-label="Close without changing preferences or granting Yandex consent">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3 mt-5 sm:pl-14">
                <button onClick={acceptAll} className="w-full sm:w-auto px-7 py-2.5 bg-[#FF8C00] hover:bg-[#E67E00] text-[#0F1419] font-bold rounded-sm transition-colors text-sm active:scale-[0.97]">
                  Accept all
                </button>
                <button onClick={acceptEssential} className="w-full sm:w-auto px-5 py-2.5 bg-white/8 hover:bg-white/15 text-white/70 hover:text-white font-medium rounded-sm transition-colors text-sm border border-white/15">
                  Decline all
                </button>
                <button onClick={openPreferences} className="w-full sm:w-auto flex items-center justify-center gap-1.5 px-4 py-2.5 text-white/40 hover:text-white/65 font-medium text-sm transition-colors">
                  <SlidersHorizontal className="w-4 h-4" />
                  Manage preferences
                </button>
              </div>

              <p className="text-xs text-white/25 mt-4 sm:pl-14">
                <a href="/privacy-policy/" className="hover:text-white/50 transition-colors">Privacy Policy</a>
                {" · "}
                <a href="/cookie-policy/" className="hover:text-white/50 transition-colors">Cookie Policy</a>
              </p>
            </>
          )}

          {panel === "preferences" && (
            <>
              <div className="flex items-center gap-3 mb-5">
                <button onClick={() => setPanel("main")} className="text-white/40 hover:text-white/70 transition-colors text-sm flex items-center gap-1">
                  ← Back
                </button>
                <h3 className="text-white font-semibold text-base flex-1">Manage Cookie Preferences</h3>
                <button onClick={closeBannerWithoutConsent} className="text-white/30 hover:text-white/60 transition-colors p-1" aria-label="Close without changing preferences or granting Yandex consent">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-3">
                {/* Essential - always on */}
                <DesktopCategory
                  label="Essential"
                  description="Required for the site to function correctly. These cannot be disabled."
                  detail="Includes session management, security tokens, and CSRF protection. No personal data is collected."
                  locked
                  expanded={showCategoryDetails === "essential"}
                  onToggleDetail={() => setShowCategoryDetails(showCategoryDetails === "essential" ? null : "essential")}
                />

                {/* Analytics */}
                <DesktopCategory
                  label="Analytics"
                  description="Help us understand how visitors interact with the site."
                  detail="Provider: Google Analytics 4 (G-V0XL3F6ZZ9). Data is anonymised and aggregated. No personal identifiers are stored."
                  checked={analyticsOn}
                  onToggle={() => setAnalyticsOn((v) => !v)}
                  expanded={showCategoryDetails === "analytics"}
                  onToggleDetail={() => setShowCategoryDetails(showCategoryDetails === "analytics" ? null : "analytics")}
                />

                <DesktopCategory
                  label="Yandex analytics and session replay"
                  description="Separate opt-in for Yandex Metrica counter 114059083. Off by default."
                  detail="Replay can record public-page content, clicks and scrolling. Forms, chat and personal results are masked. Replay is off in tools, audit flows and query-bearing pages. Analytics denial or Global Privacy Control blocks Yandex. Withdrawal stops the counter but may not remove all provider cookies."
                  checked={yandexOn}
                  onToggle={() => setYandexOn((v) => !v)}
                  expanded={showCategoryDetails === "yandex"}
                  onToggleDetail={() => setShowCategoryDetails(showCategoryDetails === "yandex" ? null : "yandex")}
                />

                {/* Advertising */}
                <DesktopCategory
                  label="Advertising"
                  description="Used to measure ad performance and show relevant ads."
                  detail="Providers: Google Ads (AW-667673408), LinkedIn Insight Tag and OpenAI Advertising Pixel (8SFENmKW28jbrKp5nRZj3g). Used for conversion tracking and retargeting."
                  checked={advertisingOn}
                  onToggle={() => setAdvertisingOn((v) => !v)}
                  expanded={showCategoryDetails === "advertising"}
                  onToggleDetail={() => setShowCategoryDetails(showCategoryDetails === "advertising" ? null : "advertising")}
                />
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3 mt-6">
                <button onClick={savePreferences} className="w-full sm:w-auto px-7 py-2.5 bg-[#FF8C00] hover:bg-[#E67E00] text-[#0F1419] font-bold rounded-sm transition-colors text-sm active:scale-[0.97]">
                  Save my preferences
                </button>
                <button onClick={acceptAll} className="w-full sm:w-auto px-5 py-2.5 bg-white/8 hover:bg-white/15 text-white/50 hover:text-white/70 font-medium rounded-sm transition-colors text-sm border border-white/10">
                  Accept all
                </button>
              </div>

              <p className="text-xs text-white/25 mt-4">
                <a href="/privacy-policy/" className="hover:text-white/50 transition-colors">Privacy Policy</a>
                {" · "}
                <a href="/cookie-policy/" className="hover:text-white/50 transition-colors">Cookie Policy</a>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────── */

function MobileCategory({
  label,
  description,
  locked,
  checked,
  onToggle,
}: {
  label: string;
  description: string;
  locked?: boolean;
  checked?: boolean;
  onToggle?: () => void;
}) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-white/8 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-white/80 text-xs font-semibold">{label}</p>
        <p className="text-white/40 text-xs leading-snug mt-0.5">{description}</p>
      </div>
      {locked ? (
        <span className="text-white/30 text-xs font-medium shrink-0 mt-0.5">Always on</span>
      ) : (
        <button
          className={`consent-toggle ${checked ? "on" : "off"}`}
          onClick={onToggle}
          aria-label={`${label} cookies ${checked ? "enabled" : "disabled"}`}
          aria-checked={checked}
          role="switch"
        />
      )}
    </div>
  );
}

function DesktopCategory({
  label,
  description,
  detail,
  locked,
  checked,
  onToggle,
  expanded,
  onToggleDetail,
}: {
  label: string;
  description: string;
  detail: string;
  locked?: boolean;
  checked?: boolean;
  onToggle?: () => void;
  expanded: boolean;
  onToggleDetail: () => void;
}) {
  return (
    <div className="rounded-sm border border-white/10 bg-white/3 px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-white/85 text-sm font-semibold">{label}</p>
          <p className="text-white/45 text-xs leading-snug mt-0.5">{description}</p>
        </div>
        {locked ? (
          <span className="text-white/30 text-xs font-medium shrink-0">Always on</span>
        ) : (
          <button
            className={`consent-toggle ${checked ? "on" : "off"}`}
            onClick={onToggle}
            aria-label={`${label} cookies ${checked ? "enabled" : "disabled"}`}
            aria-checked={checked}
            role="switch"
          />
        )}
      </div>
      <button
        onClick={onToggleDetail}
        className="flex items-center gap-1 mt-2 text-white/30 hover:text-white/55 text-xs transition-colors"
      >
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {expanded ? "Hide details" : "Show details"}
      </button>
      {expanded && (
        <p className="mt-2 text-white/40 text-xs leading-relaxed border-t border-white/8 pt-2">
          {detail}
        </p>
      )}
    </div>
  );
}
